import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import CustomerSearchResults from './CustomerSearchResults';
import './CustomerSearch.css';

export default function CustomerSearchDialog({ isOpen, initialQuery = '', onClose, availableDays, loading, locationName, selectedLocationId, onEdit, onScheduleRetouch }) {
  const [query, setQuery] = useState('');
  const [remoteDays, setRemoteDays] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState(false);

  useEffect(() => {
    if (!isOpen || query.trim().length < 3) {
      setRemoteDays([]);
      setRemoteLoading(false);
      setRemoteError(false);
      return;
    }

    const controller = new AbortController();
    setRemoteLoading(true);
    setRemoteError(false);
    setRemoteDays([]);

    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/customer-history?q=${encodeURIComponent(query.trim())}`,
          { credentials: 'same-origin', signal: controller.signal }
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const rows = await response.json();
        if (!Array.isArray(rows)) throw new Error('Invalid response');

        const days = new Map();

        rows.forEach(row => {
          const key = `${row.locationId}:${row.dayId}`;

          if (!days.has(key)) {
            days.set(key, {
              dayId: row.dayId,
              day: row.day,
              artistId: row.artistId,
              artistName: row.artistName,
              locationId: row.locationId,
              appointments: [],
            });
          }

          days.get(key).appointments.push(row);
        });

        setRemoteDays([...days.values()]);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Kundensuche:', error);
          setRemoteError(true);
        }
      } finally {
        if (!controller.signal.aborted) setRemoteLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [isOpen, query]);
  const returnFocus = useRef(true);
  useEffect(() => { const root = document.getElementById('root'); if (root) Modal.setAppElement(root); }, []);
  useEffect(() => { if (isOpen) returnFocus.current = true; setQuery(isOpen ? initialQuery : ''); }, [isOpen, initialQuery]);
  const act = (callback, retouch = false) => (...args) => {
    if (retouch && window.innerWidth <= 768) returnFocus.current = false;
    onClose(); callback(...args);
  };
  return <Modal isOpen={isOpen} onRequestClose={onClose} contentLabel="Kunde suchen"
    shouldReturnFocusAfterClose={returnFocus.current} preventScroll={true}
    className="customer-dialog" overlayClassName="customer-dialog-overlay"
    bodyOpenClassName="customer-dialog-scroll-lock" aria={{ labelledby: 'customer-dialog-title' }}>
    <header className="customer-dialog-header">
      <div><h2 id="customer-dialog-title">Kunde suchen</h2><p>{locationName || 'Aktueller Standort'} · Suche in allen 3 Studios</p></div>
      <button type="button" className="customer-close" onClick={onClose} aria-label="Kundensuche schließen">✕</button>
    </header>
    <label className="customer-dialog-field"><span>Name oder Telefonnummer</span>
      <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="z. B. Julia oder 0176…" autoFocus autoComplete="off" />
    </label>
    <div className="customer-dialog-body">
      {!query.trim() && <p className="customer-dialog-hint">Suche einen Kunden und öffne seine Termine. Du kannst Stechtermine einsehen und bearbeiten oder einen Nachstechtermin planen.</p>}
      {remoteError && <p role="alert" className="customer-search-empty">Standortübergreifende Suche derzeit nicht verfügbar.</p>}
      <CustomerSearchResults
        query={query}
        availableDays={query.trim().length >= 3 ? remoteDays : []}
        loading={remoteLoading}
        selectedLocationId={selectedLocationId}
        onEdit={act(onEdit)}
        onScheduleRetouch={act(onScheduleRetouch, true)}
      />
    </div>
  </Modal>;
}
